import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormProps,
  Input,
  Radio,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/en"; // Replace 'en' with the locale you need
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  createCampaignAsync,
  singleCampaignDataAsync,
  updateCampaignAsync,
} from "../../../store/features/EventManage/Campaigns/CampaignApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { CreateCampaignData } from "../../../types/eventManage/campaigns";
import CustomSection from "../../ui/CustomSection";
import FileUpload from "../../ui/FileUpload";
dayjs.locale("en");

export default function AddCampaigns() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [checkAll, setCheckAll] = useState(false);

  const { manageLoading, singleCampaignData } = useAppSelector(
    (state) => state.campaigns
  );

  const { login } = useAppSelector((state) => state.login);

  const statusList = [
    "Published",
    "CSO-Admin-Approved",
    "Draft",
    "Pending",
    "Cancel",
  ];

  const filteredStatusList =
    login?.role_id?.role_type === "cso_admin"
      ? statusList.filter((item) => item !== "Published")
      : statusList.filter((item) => item !== "CSO-Admin-Approved");

  const createCampaignData =
    singleCampaignData as unknown as CreateCampaignData;

  const onFinish: FormProps<any>["onFinish"] = (values) => {
    const { campaignStartDate, campaignEndDate, ...rest } = values;

    const newValue = {
      ...rest,
      campaignStartDate: campaignStartDate ? campaignStartDate.valueOf() : null,
      campaignEndDate: campaignEndDate ? campaignEndDate.valueOf() : null,
    };

    if (id) {
      dispatch(
        updateCampaignAsync({
          data: newValue,
          others: {
            reRender,
            id,
          },
        })
      );
    } else {
      dispatch(
        createCampaignAsync({
          data: newValue,
          others: {
            reRender,
          },
        })
      );
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(
        singleCampaignDataAsync({
          params: {
            reRender(val) {},
            id,
          },
        })
      );
    }
  }, [dispatch, id]);

  // add value in modal for view and edit
  useEffect(() => {
    if (id && createCampaignData?._id && manageLoading === false) {
      form.setFieldsValue({
        campaignTitle: createCampaignData?.campaignTitle,
        campaignStartDate: createCampaignData?.campaignStartDate
          ? moment(createCampaignData?.campaignStartDate)
          : null,
        campaignEndDate: createCampaignData?.campaignEndDate
          ? moment(createCampaignData?.campaignEndDate)
          : null,
        campaignOrganizedBy: createCampaignData?.campaignOrganizedBy,
        campaignHostBy: createCampaignData?.campaignHostBy,
        campaignAddress: createCampaignData?.campaignAddress,
        campaignBanner: createCampaignData?.campaignBanner,
        campaignStatus: createCampaignData?.campaignStatus,
      });
    }
  }, [
    createCampaignData?._id,
    createCampaignData?.campaignAddress,
    createCampaignData?.campaignBanner,
    createCampaignData?.campaignEndDate,
    createCampaignData?.campaignHostBy,
    createCampaignData?.campaignOrganizedBy,
    createCampaignData?.campaignStartDate,
    createCampaignData?.campaignStatus,
    createCampaignData?.campaignTitle,
    form,
    id,
    manageLoading,
  ]);

  function reRender(val: boolean) {
    if (val) {
      navigate("/admin/event-manage/campaigns");
    }
  }

  const handleCheckAll = (e: any) => {
    const checked = e.target.checked;
    setCheckAll(checked);
  };

  return (
    <AdminLayout
      title={`${id ? "Campaign Update" : "Create a New Campaign"}`}
      backLink="/admin/event-manage/campaigns"
    >
      <Form
        className="custom-form space-y-[14px]"
        layout="vertical"
        onFinish={onFinish}
        size="large"
        form={form}
      >
        <CustomSection>
          <div className="grid grid-cols-2 gap-x-2.5 w-full">
            <Form.Item
              name="campaignTitle"
              label="Campaign Title"
              rules={[
                {
                  required: true,
                  message: "Please select title",
                },
              ]}
            >
              <Input placeholder="Enter campaign title" size="large" />
            </Form.Item>

            <Form.Item
              name="campaignStartDate"
              label="Campaign Start Date & Time"
              rules={[
                {
                  required: true,
                  message: "Enter date and time",
                },
              ]}
            >
              <DatePicker
                format="DD MMM, YYYY hh:mm A"
                className="w-full"
                size="large"
                showTime={{
                  format: "hh:mm A",
                  use12Hours: true,
                }}
                placeholder="DD MM YYYY hh:mm AM/PM"
              />
            </Form.Item>

            <Form.Item
              name="campaignEndDate"
              label="Campaign End Date & Time"
              rules={[
                {
                  required: true,
                  message: "Enter date and time",
                },
              ]}
            >
              <DatePicker
                format="DD MMM, YYYY hh:mm A"
                className="w-full"
                size="large"
                showTime={{
                  format: "hh:mm A",
                  use12Hours: true,
                }}
                placeholder="DD MM YYYY hh:mm AM/PM"
              />
            </Form.Item>

            <Form.Item
              name="campaignOrganizedBy"
              label="Organized By"
              rules={[
                {
                  required: true,
                  message: "Please organization name",
                },
              ]}
            >
              <Input placeholder="Enter organization name" size="large" />
            </Form.Item>

            <Form.Item
              name="campaignHostBy"
              label="Host By"
              rules={[
                {
                  required: true,
                  message: "Please enter host name",
                },
              ]}
            >
              <Input placeholder="Enter host name" size="large" />
            </Form.Item>

            <Form.Item
              name="campaignAddress"
              label="Campaign Address"
              rules={[
                {
                  required: true,
                  message: "Please enter address",
                },
              ]}
            >
              <Input placeholder="Address" size="large" />
            </Form.Item>

            <Form.Item
              name="campaignBanner"
              label="Campaign Banner"
              valuePropName="fileUrl"
              rules={[
                {
                  required: true,
                  message: "Please upload photo",
                },
              ]}
            >
              <FileUpload
                form={form}
                accept={acceptFile}
                name="campaignBanner"
                onChange={(url) =>
                  form.setFieldsValue({ campaignBanner: url || undefined })
                }
              />
            </Form.Item>
          </div>
        </CustomSection>

        <CustomSection>
          <div className="flex items-center gap-x-2.5 mb-4">
            <Form.Item
              name="campaignStatus"
              label="Campaign Status"
              rules={[
                {
                  required: true,
                  message: "Please select status",
                },
              ]}
            >
              <Radio.Group
                className="custom-radio"
                //   onChange={(e) => setOwnershipType(e.target.value)}
                size="large"
              >
                {filteredStatusList?.map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="border border-solid border-[#d7dee8] mt-1 mb-4 w-full" />

          <div className="w-full justify-between items-center flex ">
            <Checkbox checked={checkAll} onChange={handleCheckAll}>
              <span className="text-body text-sm font-medium leading-normal">
                Yes! I confirmed and Publish this campaign
              </span>
            </Checkbox>

            <div className="justify-center items-center inline-flex gap-2 pt-6 ">
              <Button onClick={() => navigate(-1)} className="cancel-btn">
                Cancel
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                loading={manageLoading}
                disabled={
                  !checkAll ||
                  form.getFieldsError().some(({ errors }) => errors.length > 0)
                }
              >
                {id ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </CustomSection>
      </Form>
    </AdminLayout>
  );
}

const acceptFile = ".jpg, .jpeg, .png";
